"use client";

import Image from "next/image";
import Link from "next/link";
import FadeIn from "@/components/ui/FadeIn";
import TiltCard from "@/components/ui/TiltCard";
import { cdnUrl } from "@/lib/cdn";

const products = [
  {
    title: "Error-Free Numbers",
    href: "/products#co-analyst",
    description:
      "Convert unstructured data into insight. Reliably pull figures, quotes and analysis from 250M+ SEC filings, presentations and conference calls.",
    image: cdnUrl("/images/product/co-analyst-screenshot.webp"),
    tag: "In-house AI",
    stat: "10x",
    statLabel: "Faster research",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
      </svg>
    ),
  },
  {
    title: "AI-Powered Search",
    href: "/products#market-intelligence",
    description:
      "Track themes and inflection points in SEC filings and conferences calls with high-precision AI-powered search.",
    image: cdnUrl("/images/product/market-intelligence-screenshot.webp"),
    tag: "Proprietary Retrieval",
    stat: "10K+",
    statLabel: "Companies covered",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
      </svg>
    ),
  },
  {
    title: "Forensic Risk",
    href: "/products#forensic",
    description:
      "Securities class action prediction, risk monitoring and accounting fraud assessment",
    image: cdnUrl("/images/product/forensic-risk.webp"),
    tag: "Quantified Risk",
    stat: "3x",
    statLabel: "Lawsuit Risk",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
];

export default function ProductCards({ exclude }: { exclude?: string[] } = {}) {
  const filtered = exclude?.length
    ? products.filter((p) => !exclude.includes(p.title))
    : products;
  return (
    <div className={`grid sm:grid-cols-2 ${filtered.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-5 sm:gap-6`}>
      {filtered.map((product, i) => (
        <FadeIn key={product.title} delay={i * 0.15}>
          <TiltCard className="h-full">
            <Link
              href={product.href}
              className="group relative h-full rounded-xl border border-black/[0.06] bg-white shadow-[0_0_40px_-8px_rgba(139,92,246,0.08)] transition-all duration-500 hover:border-violet-500/20 hover:shadow-[0_0_60px_-4px_rgba(139,92,246,0.15)] block"
            >
              {/* Image */}
              <div className="aspect-[16/10] overflow-hidden relative rounded-t-xl">
                <Image
                  src={product.image}
                  alt={product.title}
                  width={800}
                  height={600}
                  sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 400px"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
                {/* Stat overlay */}
                <div className="absolute bottom-4 left-6">
                  <p className="text-3xl font-[family-name:var(--font-display)] text-black/70">{product.stat}</p>
                  <p className="text-xs text-black/40 tracking-wider uppercase">{product.statLabel}</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 lg:p-8">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-violet-800 bg-violet-50 rounded-full border border-violet-100 mb-4">
                  {product.icon}
                  {product.tag}
                </span>
                <h3 className="text-lg font-semibold tracking-tight mb-2">
                  {product.title}
                </h3>
                <p className="text-sm text-black/50 leading-relaxed">
                  {product.description}
                </p>
                <div className="mt-5 flex items-center gap-2 text-sm font-medium text-violet-800 group-hover:text-violet-600 transition-colors duration-500">
                  <span>Explore</span>
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>
            </Link>
          </TiltCard>
        </FadeIn>
      ))}
    </div>
  );
}
